import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Clock, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import blogArticles, { BLOG_IMAGES } from '@/data/blogArticles';
import SEOHead from '@/components/SEOHead';

const categories = ['Semua', ...new Set(blogArticles.map(a => a.category))];

const BlogPage = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('Semua');

  const filtered = useMemo(() => {
    return blogArticles.filter(a => {
      const matchSearch = !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase());
      const matchCat = activeCategory === 'Semua' || a.category === activeCategory;
      return matchSearch && matchCat;
    });
  }, [search, activeCategory]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-zinc-900">
      <SEOHead
        title="Blog & Panduan — Nerava"
        description="Tips jualan online, panduan auto reply WhatsApp, Instagram & Messenger, dan strategi AI untuk UMKM Indonesia."
        canonical="https://nerava.id/blog"
      />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20 dark:bg-zinc-900">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="sm"><ArrowLeft className="w-5 h-5" /></Button>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" onError={e => e.target.style.display = 'none'} />
            <span className="font-bold text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>Nerava Blog</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-3 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
            Blog & Panduan
          </h1>
          <p className="text-slate-500 max-w-xl mx-auto dark:text-zinc-400">Tips jualan online, panduan auto reply, dan strategi AI untuk UMKM Indonesia</p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-zinc-400" />
            <input
              type="text"
              placeholder="Cari artikel..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-zinc-900"
              data-testid="blog-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8 justify-center flex-wrap">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeCategory === cat
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-300'
              } dark:bg-zinc-900`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(article => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="bg-white rounded-xl border border-slate-200 hover:shadow-lg hover:border-indigo-200 transition-all duration-200 overflow-hidden group dark:bg-zinc-900"
              data-testid={`blog-card-${article.id}`}
            >
              <div className="h-40 bg-gradient-to-br from-blue-500 to-indigo-600 overflow-hidden">
                {BLOG_IMAGES[article.category] ? (
                  <img src={BLOG_IMAGES[article.category]} alt={article.title} className="w-full h-full object-cover opacity-90" />
                ) : (
                  <div className="flex items-center justify-center h-full p-6">
                    <h3 className="text-white font-semibold text-center text-sm sm:text-base leading-snug line-clamp-3">
                      {article.title}
                    </h3>
                  </div>
                )}
              </div>
              <div className="p-4 sm:p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-medium dark:text-indigo-400 dark:bg-indigo-950/40">{article.category}</span>
                  <span className="text-xs text-slate-500 flex items-center gap-1 dark:text-zinc-400"><Clock className="w-3 h-3" />{article.readTime}</span>
                </div>
                <p className="text-sm text-slate-600 line-clamp-2 mb-3">{article.excerpt}</p>
                <span className="text-sm text-indigo-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all dark:text-indigo-400">
                  Baca selengkapnya <ChevronRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-slate-500 dark:text-zinc-400">
            <p>Tidak ada artikel yang ditemukan.</p>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl p-8 sm:p-12 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Siap Otomasi Customer Service Anda?</h2>
          <p className="text-blue-100 mb-6 max-w-lg mx-auto">Coba Nerava gratis 30 hari. AI chatbot untuk WhatsApp, Instagram, Messenger, dan TikTok Shop.</p>
          <Link to="/register">
            <Button className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-8 py-3 dark:bg-zinc-900 dark:text-indigo-400">
              Coba Gratis Sekarang
            </Button>
          </Link>
        </div>

        <div className="mt-12 text-center">
          <p className="text-slate-500 text-sm dark:text-zinc-400">© 2026 Nerava.id. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default BlogPage;
